import { useEffect, useMemo, useRef, useState } from "react";
import {
  queueMobileScrollSnap,
  setJourneyMobileScrollGate,
} from "@/journeyScrollGate";
import { MOBILE_ANCHOR_NAV_EVENT } from "@/mobileAnchorNav";
import { mobileStoryHashesBelowStory } from "@/mobileStoryNav";

const MOBILE_AI_BELOW_HASHES = mobileStoryHashesBelowStory("#ai-tutor");
const MOBILE_AI_FINAL_STEP = 4;
import { observeSectionActive } from "@/sectionVisibility";
import { getAssetUrl } from "@/lib/assetBaseUrl";

const ASSETS = {
  aiIntro:
    "https://asset.sparkl.me/pb/poc-folder/TH8M7p8EKyIz7MzKnWVKfgEAvNzMpmkDQmxM2zWP.mp4",
  aiTeaching:
    "https://asset.sparkl.me/pb/poc-folder/ZvFFp15ZDyC1bELNWqCR7mCST99Sdyv2chtWqr6R.mp4",
  aiDoubt:
    "https://asset.sparkl.me/pb/poc-folder/TH8M7p8EKyIz7MzKnWVKfgEAvNzMpmkDQmxM2zWP.mp4",
  slide:
    "https://asset.sparkl.me/pb/poc-folder/rRrNMHcV5UBy0OoaWFr2OEsnSiB1BKniBOizjmkd.mp4",
  activityPath:
    "pb/assets/video/OlepKTQGMcSVFy3jL4BDDIRNcEluUvFcpcuxrvUT.mp4",
  handwritten:
    "https://asset.sparkl.me/pb/poc-folder/fCLzzXAokZzGhcUYKWaLW7gfWT3cMDuYtIE48nCB.mp4",
  student:
    "https://asset.sparkl.me/pb/poc-folder/wAbwhtj6gLqa1aWz1wZGEs2lG0NpoBHJVpwOfpXW.mp4",
  studentDoubt:
    "https://asset.sparkl.me/pb/poc-folder/5lZZlwNvcuiNO8W17xga4fVr4K9nyJ9VJEX1IdlU.mp4",
  handIcon:
    "https://asset.sparkl.me/pb/poc-folder/dTI0MLZzL3n61TXw7SFpc7Rjz4egAcPDhps2sFUF.png",
};

// Step boundaries inside the normalised section scroll [0..1].
// 5 equal phases. CSS section height (~520vh desktop / ~390vh ≤768px).
const STEP_BOUNDS = [0, 0.2, 0.4, 0.6, 0.8, 1.0];

const TRANSCRIPTS = [
  "Today, let's understand photosynthesis in a simple way. Think of a plant as a tiny food factory that uses sunlight to make its own food.",
  "Plants take in sunlight, carbon dioxide, and water. Then, inside the leaves, they use these to make glucose, which is their food.",
  "Now try placing each input in the right place. Sunlight goes to the leaf, water comes from the roots, and carbon dioxide enters through the leaves.",
  "Good attempt. Your idea is mostly correct, but this step needs a small correction. Water is absorbed by the roots, not directly by the leaves.",
  "Great question. Plants need sunlight because sunlight provides the energy required to turn water and carbon dioxide into glucose.",
];

const EXPLAINERS = [
  { title: "AI Tutor starts the lesson", body: "The AI Tutor begins teaching like a real learning companion." },
  { title: "Teaching with animated slides", body: "Photosynthesis is explained with a visual slide while the student watches." },
  { title: "Interactive activity", body: "The student places each input into the right part of the plant." },
  { title: "Checks handwritten answers", body: "AI highlights correct and incorrect steps on the student's written work." },
  { title: "Clears doubts in real time", body: "The AI answers the student's doubt instantly, while the doubt is still fresh." },
];

// Layer states per step. Positions are in percentage of the workspace
// frame. Each entry = { l, t, w, h, o[, s] }.
// l/t/w/h are left/top/width/height as %, o is opacity, s optional scale.
const S_AI_AVATAR = [
  { l: 27, t: 12, w: 46, h: 76, o: 1 }, // 0: large center
  { l: 7,  t: -8, w: 17, h: 28, o: 1 }, // 1: taller AI tutor, bottom-right dips into slide
  { l: 7,  t: -8, w: 17, h: 28, o: 1 }, // 2
  { l: 7,  t: -8, w: 17, h: 28, o: 1 }, // 3
  { l: 6,  t: 14, w: 40, h: 60, o: 1 }, // 4: left equal
];

const S_MAIN = [
  { l: 20, t: 10, w: 64, h: 66, o: 0 }, // 0: hidden (centered base)
  { l: 20, t: 6,  w: 64, h: 70, o: 1 }, // 1: slide — larger
  { l: 20, t: 6,  w: 64, h: 70, o: 1 }, // 2: activity
  { l: 20, t: 6,  w: 64, h: 70, o: 1 }, // 3: handwritten
  { l: 20, t: 10, w: 64, h: 66, o: 0 }, // 4: hidden
];

const S_STUDENT = [
  { l: 50, t: 60, w: 0,  h: 0,  o: 0 }, // 0: hidden
  { l: 78, t: 72, w: 14, h: 22, o: 1 }, // 1: top-left dips into slide's bottom-right
  { l: 78, t: 72, w: 14, h: 22, o: 1 }, // 2
  { l: 78, t: 72, w: 14, h: 22, o: 1 }, // 3
  { l: 54, t: 14, w: 40, h: 60, o: 1 }, // 4: right equal (doubt step)
];

const S_HAND = [
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 1, s: 1 },
];

// MOBILE layer states — teaching PiP (steps 1–3): center of AI tile meets the
// center video's top-left corner (S_MAIN_M ~16%, 34%) => l = 16−w/2, t = 34−h/2.
const S_AI_AVATAR_M = [
  { l: 20, t: 14, w: 60, h: 45, o: 1 }, // 0: intro — large centered
  { l: 2, t: 22, w: 28, h: 24, o: 1 }, // 1–3: center of PiP on main card TL (16,34)
  { l: 2, t: 22, w: 28, h: 24, o: 1 },
  { l: 2, t: 22, w: 28, h: 24, o: 1 },
  { l: 5,  t: 18, w: 42, h: 62, o: 1 }, // 4: doubt — left equal
];

const S_MAIN_M = [
  { l: 18, t: 26, w: 64, h: 48, o: 0 },  // 0: hidden
  { l: 16, t: 34, w: 68, h: 52, o: 1 },  // 1: slide centered, below AI
  { l: 16, t: 34, w: 68, h: 52, o: 1 },  // 2: activity
  { l: 16, t: 34, w: 68, h: 52, o: 1 },  // 3: handwritten
  { l: 18, t: 26, w: 64, h: 48, o: 0 },  // 4: hidden
];

const S_STUDENT_M = [
  { l: 50, t: 60, w: 0,  h: 0,  o: 0 },  // 0: hidden
  { l: 63, t: 71, w: 34, h: 26, o: 1 },  // 1-3: bottom-right, 16:9
  { l: 63, t: 71, w: 34, h: 26, o: 1 },
  { l: 63, t: 71, w: 34, h: 26, o: 1 },
  { l: 53, t: 18, w: 42, h: 62, o: 1 },  // 4: right equal (doubt)
];

const S_HAND_M = [
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 0, s: 0.5 },
  { l: 46, t: 2,  w: 8, h: 12, o: 1, s: 1 },
];

// Per-step opacities for the 3 stackable main videos (slide/activity/handwritten)
const S_SLIDE_O = [0, 1, 0, 0, 0];
const S_ACT_O   = [0, 0, 1, 0, 0];
const S_HW_O    = [0, 0, 0, 1, 0];
// Per-step opacities for AI avatar tracks (intro vs teaching vs doubt)
const S_AI_INTRO_O    = [1, 0, 0, 0, 0];
const S_AI_TEACH_O    = [0, 1, 1, 1, 0];
const S_AI_DOUBT_O    = [0, 0, 0, 0, 1];
// Student track swap (teaching vs doubt)
const S_STU_TEACH_O = [0, 1, 1, 1, 0];
const S_STU_DOUBT_O = [0, 0, 0, 0, 1];

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
function ease(t) { return t * t * (3 - 2 * t); }

/** Paired with SparklLiveSection — softer follow vs raw wheel/touch. */
const AI_SCROLL_SMOOTH_DESKTOP = 0.14;
/** Desktop: intro opacity ramps with normalized section scroll.p. Mobile uses viewport ingress instead. */
const SECTION_INTRO_SCROLL = 0.1;

/** Mobile — discrete steps (one swipe = one step transition). */
const MOBILE_AI_ANIM_EPS = 0.008;
const MOBILE_AI_BAND_EPS = 0.004;
/** Normalised p per second — ~0.26s per step band. */
const MOBILE_AI_ANIM_SPEED = 0.78;
const MOBILE_AI_LEAVE_TOP_PX = 80;
const MOBILE_AI_SCROLL_GATE_OWNER = "ai-tutor";
/** Settled p per step — past each step's 50% transition midpoint. */
const MOBILE_AI_STEP_P = STEP_BOUNDS.slice(0, -1).map((start, i) => {
  const end = STEP_BOUNDS[i + 1];
  return start + 0.5 * (end - start);
});

/**
 * Clamp Lenis scroll while pinned so one swipe cannot skip past the next step band.
 * Mirrors JourneySection mobile band gating.
 */
function mobileAiMaxScrollP(displayStep, animating) {
  if (displayStep < 0) {
    return STEP_BOUNDS[1] - MOBILE_AI_BAND_EPS;
  }
  if (displayStep >= 4) {
    return 1 - MOBILE_AI_BAND_EPS;
  }
  // Idle: allow entering the next band only. Animating: lock to the current band.
  const maxBandIdx = animating ? displayStep + 1 : displayStep + 2;
  return STEP_BOUNDS[maxBandIdx] - MOBILE_AI_BAND_EPS;
}

function mobileAiMinScrollP(displayStep, animating, allowExitUp) {
  if (displayStep <= 0) {
    return allowExitUp ? null : 0;
  }
  if (displayStep >= 4) {
    return STEP_BOUNDS[4] + MOBILE_AI_BAND_EPS;
  }
  // Floor at the current band while animating; idle may scroll into the previous band.
  if (animating) {
    return STEP_BOUNDS[displayStep] + MOBILE_AI_BAND_EPS;
  }
  return STEP_BOUNDS[displayStep - 1] + MOBILE_AI_BAND_EPS;
}

function mobileAiSnapScrollY(sectionEl, displayStep, scrollRange) {
  const pinStartY = sectionEl.offsetTop;
  const bandP =
    displayStep <= 0
      ? MOBILE_AI_BAND_EPS
      : STEP_BOUNDS[displayStep] + MOBILE_AI_BAND_EPS;
  return pinStartY + bandP * scrollRange;
}

function mobileAiScrollStepFromP(p) {
  for (let i = 0; i < STEP_BOUNDS.length - 1; i += 1) {
    if (p < STEP_BOUNDS[i + 1]) return i;
  }
  return STEP_BOUNDS.length - 2;
}

/** True when scroll has crossed exactly one band ahead/behind the committed step. */
function mobileAiShouldAdvanceStep(displayStep, scrollStep, idle) {
  if (!idle || displayStep < 0 || scrollStep < 0) return 0;
  if (scrollStep === displayStep + 1 && displayStep < 4) return 1;
  if (scrollStep === displayStep - 1 && displayStep > 0) return -1;
  return 0;
}

function aiStepTargetP(step) {
  if (step <= 0) return MOBILE_AI_STEP_P[0];
  return MOBILE_AI_STEP_P[Math.min(step, MOBILE_AI_STEP_P.length - 1)];
}

function clampAiUiStep(step) {
  return Math.min(Math.max(step, 0), EXPLAINERS.length - 1);
}

/**
 * For scroll progress p, return { step, local } where `step` is the active
 * step index (0..4) and `local` is progress within that step [0..1].
 */
function stepOf(p) {
  for (let i = 0; i < STEP_BOUNDS.length - 1; i += 1) {
    const s = STEP_BOUNDS[i];
    const e = STEP_BOUNDS[i + 1];
    if (p >= s && p < e) {
      return { step: i, local: (p - s) / (e - s) };
    }
  }
  return { step: 4, local: 1 };
}

/**
 * Interpolated numeric state for a layer. Within each beat the first 30%
 * is a smoothstep transition from the previous beat's state; the remaining
 * 70% holds the beat's target state.
 */
function interpArr(arr, p) {
  const { step, local } = stepOf(p);
  const TRANS = 0.5;
  if (step === 0 || local >= TRANS) return arr[step];
  const t = ease(local / TRANS);
  const a = arr[step - 1];
  const b = arr[step];
  return a + (b - a) * t;
}
function interpState(stateArr, p) {
  return {
    l: interpArr(stateArr.map((x) => x.l), p),
    t: interpArr(stateArr.map((x) => x.t), p),
    w: interpArr(stateArr.map((x) => x.w), p),
    h: interpArr(stateArr.map((x) => x.h), p),
    o: interpArr(stateArr.map((x) => x.o), p),
    s: interpArr(stateArr.map((x) => x.s ?? 1), p),
  };
}
function interpO(arr, p) {
  return interpArr(arr, p);
}

function applyState(el, st) {
  if (!el) return;
  el.style.left = `${st.l}%`;
  el.style.top = `${st.t}%`;
  el.style.width = `${st.w}%`;
  el.style.height = `${st.h}%`;
  el.style.opacity = String(st.o);
  if (st.s !== undefined) {
    el.style.transform = `scale(${st.s})`;
  }
}

export default function SparklAITutorSection() {
  const sectionRef = useRef(null);
  const introBlockRef = useRef(null);
  const titleRef = useRef(null);
  const shellRef = useRef(null);
  const explainerRef = useRef(null);
  const transcriptRef = useRef(null);

  const aiIntroRef = useRef(null);
  const aiTeachRef = useRef(null);
  const aiDoubtRef = useRef(null);
  const slideRef = useRef(null);
  const actRef = useRef(null);
  const hwRef = useRef(null);
  const studentTeachRef = useRef(null);
  const studentDoubtRef = useRef(null);
  const handRef = useRef(null);

  // Wrapper refs per layer-group (avatar/main/student/hand) — holds position
  const avatarWrapRef = useRef(null);
  const mainWrapRef = useRef(null);
  const studentWrapRef = useRef(null);
  const handWrapRef = useRef(null);

  const mobAiStepRef = useRef(-1);
  const mobAiVisualPRef = useRef(0);
  const mobAiEnteredFromBelowRef = useRef(false);
  const lastTimeRef = useRef(performance.now());

  const [step, setStep] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  const reduceMotionRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return undefined;
    const onAnchorNav = (e) => {
      const hash = e.detail?.hash;
      if (hash === "#ai-tutor") {
        mobAiEnteredFromBelowRef.current = false;
        mobAiStepRef.current = -1;
        mobAiVisualPRef.current = aiStepTargetP(0);
        setStep(0);
        setWordCount(0);
        return;
      }
      if (hash && MOBILE_AI_BELOW_HASHES.has(hash)) {
        mobAiEnteredFromBelowRef.current = true;
        mobAiStepRef.current = -1;
        mobAiVisualPRef.current = 0;
        setStep(0);
        setWordCount(0);
      }
    };
    window.addEventListener(MOBILE_ANCHOR_NAV_EVENT, onAnchorNav);
    return () => window.removeEventListener(MOBILE_ANCHOR_NAV_EVENT, onAnchorNav);
  }, [isMobile]);

  useEffect(() => {
    try {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const sync = () => {
        reduceMotionRef.current = mq.matches;
      };
      sync();
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    } catch {
      reduceMotionRef.current = false;
      return undefined;
    }
  }, []);

  // Memo split of each transcript into words (to avoid per-frame split).
  const transcriptWords = useMemo(
    () => TRANSCRIPTS.map((t) => t.split(/\s+/)),
    []
  );

  useEffect(() => {
    const sec = sectionRef.current;
    if (!sec) return undefined;

    // smoothP: lerp-smoothed scroll progress (desktop only).
    let smoothP = 0;
    let rafId = 0;
    let lastStep = -1;

    const loop = () => {
      rafId = requestAnimationFrame(loop);

      const rect = sec.getBoundingClientRect();
      const total = sec.offsetHeight - window.innerHeight;
      const targetP = clamp01(-rect.top / Math.max(total, 1));

      const now = performance.now();
      const dt = Math.min(0.1, (now - lastTimeRef.current) / 1000);
      lastTimeRef.current = now;

      let p;
      let uiStep;

      if (isMobile) {
        const aiInView =
          rect.bottom > 0 && rect.top < window.innerHeight;
        const leftUpward = rect.top > MOBILE_AI_LEAVE_TOP_PX;
        let displayStep = mobAiStepRef.current;
        let visualP = mobAiVisualPRef.current;
        const prevDisplayStep = displayStep;
        let queueFinaleEntrySnap = false;

        let scrollStep = -1;
        if (aiInView && (rect.top <= 0 || displayStep >= 0)) {
          scrollStep = mobileAiScrollStepFromP(targetP);
        }

        const idleAtCurrentStep =
          displayStep >= 0 &&
          Math.abs(visualP - aiStepTargetP(displayStep)) < MOBILE_AI_ANIM_EPS;
        const mayResetUpward =
          leftUpward &&
          displayStep <= 0 &&
          (displayStep < 0 || idleAtCurrentStep || scrollStep <= 0);
        const mayResetPast =
          rect.bottom <= 0 &&
          (displayStep < 0 ||
            Math.abs(visualP - aiStepTargetP(displayStep)) <
              MOBILE_AI_ANIM_EPS);

        if (mayResetUpward) {
          mobAiEnteredFromBelowRef.current = false;
          displayStep = -1;
          visualP = 0;
        } else if (mayResetPast) {
          mobAiEnteredFromBelowRef.current = true;
          displayStep = -1;
          visualP = 0;
        } else if (displayStep < 0 && rect.top <= 0) {
          if (mobAiEnteredFromBelowRef.current) {
            displayStep = MOBILE_AI_FINAL_STEP;
            visualP = aiStepTargetP(MOBILE_AI_FINAL_STEP);
            mobAiEnteredFromBelowRef.current = false;
            queueFinaleEntrySnap = true;
          } else {
            const entryStep = mobileAiScrollStepFromP(targetP);
            displayStep = entryStep;
            visualP = aiStepTargetP(entryStep);
          }
        } else if (dt > 0 && displayStep >= 0) {
          const targetVisual = aiStepTargetP(displayStep);
          const idle =
            Math.abs(visualP - targetVisual) < MOBILE_AI_ANIM_EPS;
          const advanceDir = mobileAiShouldAdvanceStep(
            displayStep,
            scrollStep,
            idle,
          );
          if (advanceDir !== 0 && !queueFinaleEntrySnap) {
            displayStep += advanceDir;
          } else if (
            !queueFinaleEntrySnap &&
            !idle &&
            scrollStep < displayStep &&
            displayStep > 0
          ) {
            displayStep -= 1;
          }

          const goalP = aiStepTargetP(displayStep);
          const diff = goalP - visualP;
          const stepDelta = MOBILE_AI_ANIM_SPEED * dt;
          if (Math.abs(diff) <= stepDelta) {
            visualP = goalP;
          } else {
            visualP += Math.sign(diff) * stepDelta;
          }
        }

        mobAiStepRef.current = displayStep;
        mobAiVisualPRef.current = visualP;
        const rawUiStep =
          displayStep >= 0
            ? displayStep
            : aiInView
              ? mobileAiScrollStepFromP(targetP)
              : 0;
        uiStep = clampAiUiStep(rawUiStep);
        p =
          displayStep < 0 && aiInView
            ? aiStepTargetP(uiStep)
            : visualP;

        const aiPinnedActive = aiInView && rect.top <= 0;
        const animating =
          displayStep >= 0 &&
          Math.abs(visualP - aiStepTargetP(displayStep)) >= MOBILE_AI_ANIM_EPS;
        const settledAtStep = displayStep >= 0 && !animating;
        const allowExitUp =
          settledAtStep && displayStep <= 0 && scrollStep <= 0;
        const allowUpwardLeave =
          allowExitUp || (leftUpward && idleAtCurrentStep);
        const allowForwardExit = displayStep >= 4 && settledAtStep;

        if (
          aiPinnedActive &&
          displayStep >= 0 &&
          !allowForwardExit &&
          !allowUpwardLeave
        ) {
          const maxP = mobileAiMaxScrollP(displayStep, animating);
          const minP = mobileAiMinScrollP(
            displayStep,
            animating,
            allowExitUp,
          );
          const pinStartY = sec.offsetTop;
          const sectionEndY = pinStartY + total;
          const minScrollY =
            minP != null ? pinStartY + minP * total : pinStartY;
          const maxScrollY = Math.min(
            pinStartY + maxP * total,
            sectionEndY,
          );
          setJourneyMobileScrollGate(
            true,
            minScrollY,
            maxScrollY,
            pinStartY,
            sectionEndY,
            MOBILE_AI_SCROLL_GATE_OWNER,
          );

          if (queueFinaleEntrySnap) {
            queueMobileScrollSnap(
              mobileAiSnapScrollY(sec, MOBILE_AI_FINAL_STEP, total),
              MOBILE_AI_SCROLL_GATE_OWNER,
            );
          } else if (displayStep !== prevDisplayStep && displayStep >= 0) {
            queueMobileScrollSnap(
              mobileAiSnapScrollY(sec, displayStep, total),
              MOBILE_AI_SCROLL_GATE_OWNER,
            );
          } else if (
            settledAtStep &&
            scrollStep > displayStep + 1 &&
            displayStep < 4
          ) {
            queueMobileScrollSnap(
              mobileAiSnapScrollY(sec, displayStep + 1, total),
              MOBILE_AI_SCROLL_GATE_OWNER,
            );
          } else if (
            settledAtStep &&
            scrollStep < displayStep - 1 &&
            displayStep > 0
          ) {
            queueMobileScrollSnap(
              mobileAiSnapScrollY(sec, displayStep - 1, total),
              MOBILE_AI_SCROLL_GATE_OWNER,
            );
          }
        } else {
          setJourneyMobileScrollGate(
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            MOBILE_AI_SCROLL_GATE_OWNER,
          );
        }
      } else {
        smoothP += (targetP - smoothP) * AI_SCROLL_SMOOTH_DESKTOP;
        p = smoothP;
        uiStep = stepOf(p).step;
      }

      if (rect.top > window.innerHeight + 50 || rect.bottom < -50) {
        if (isMobile) {
          setJourneyMobileScrollGate(
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            MOBILE_AI_SCROLL_GATE_OWNER,
          );
        }
        return;
      }
      const rm = reduceMotionRef.current;
      const introScrollLine = clamp01(p / SECTION_INTRO_SCROLL);

      if (introBlockRef.current) {
        if (isMobile || rm) {
          introBlockRef.current.style.opacity = "1";
        } else {
          introBlockRef.current.style.opacity = String(introScrollLine);
        }
      }

      if (titleRef.current) titleRef.current.style.removeProperty("transform");
      if (shellRef.current) shellRef.current.style.removeProperty("transform");

      const AV = isMobile ? S_AI_AVATAR_M : S_AI_AVATAR;
      const MN = isMobile ? S_MAIN_M : S_MAIN;
      const ST = isMobile ? S_STUDENT_M : S_STUDENT;
      const HD = isMobile ? S_HAND_M : S_HAND;
      applyState(avatarWrapRef.current, interpState(AV, p));
      applyState(mainWrapRef.current, interpState(MN, p));
      applyState(studentWrapRef.current, interpState(ST, p));
      applyState(handWrapRef.current, interpState(HD, p));

      if (aiIntroRef.current) aiIntroRef.current.style.opacity = String(interpO(S_AI_INTRO_O, p));
      if (aiTeachRef.current) aiTeachRef.current.style.opacity = String(interpO(S_AI_TEACH_O, p));
      if (aiDoubtRef.current) aiDoubtRef.current.style.opacity = String(interpO(S_AI_DOUBT_O, p));
      if (slideRef.current) slideRef.current.style.opacity = String(interpO(S_SLIDE_O, p));
      if (actRef.current) actRef.current.style.opacity = String(interpO(S_ACT_O, p));
      if (hwRef.current) hwRef.current.style.opacity = String(interpO(S_HW_O, p));
      if (studentTeachRef.current) studentTeachRef.current.style.opacity = String(interpO(S_STU_TEACH_O, p));
      if (studentDoubtRef.current) studentDoubtRef.current.style.opacity = String(interpO(S_STU_DOUBT_O, p));

      const { step: curStep } = stepOf(p);
      const nextStep = clampAiUiStep(isMobile ? uiStep : curStep);
      if (nextStep !== lastStep) {
        lastStep = nextStep;
        setStep(nextStep);
      }
    };

    rafId = requestAnimationFrame(loop);

    return () => {
      if (isMobile) {
        setJourneyMobileScrollGate(
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          MOBILE_AI_SCROLL_GATE_OWNER,
        );
      }
      cancelAnimationFrame(rafId);
    };
  }, [transcriptWords, isMobile]);

  // Time-driven transcript reveal — mimics natural speech pacing. Whenever
  // the active step changes, reset to 0 and advance one word every 180ms.
  useEffect(() => {
    const words = transcriptWords[step] || [];
    setWordCount(0);
    if (words.length === 0) return undefined;
    let i = 0;
    const tick = () => {
      i += 1;
      setWordCount(i);
    };
    const id = setInterval(() => {
      if (i >= words.length) {
        clearInterval(id);
        return;
      }
      tick();
    }, 180);
    return () => clearInterval(id);
  }, [step, transcriptWords]);

  // Desktop: autoplay on mount. Mobile: play only while section is on-screen.
  useEffect(() => {
    const refs = [
      aiIntroRef, aiTeachRef, aiDoubtRef,
      slideRef, actRef, hwRef,
      studentTeachRef, studentDoubtRef,
    ];

    const syncPlayback = (playing) => {
      refs.forEach((r) => {
        const v = r.current;
        if (!v) return;
        if (!playing) {
          try {
            v.pause();
          } catch {
            /* noop */
          }
          return;
        }
        try {
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch {
          /* noop */
        }
      });
    };

    if (!isMobile) {
      syncPlayback(true);
      return undefined;
    }

    const sec = sectionRef.current;
    if (!sec) return undefined;

    return observeSectionActive(sec, syncPlayback, "20% 0px");
  }, [isMobile]);

  // Physics-pop card swap. Each step has at most one active "main" video
  // (slide=1, activity=2, handwritten=3). When the step changes, fire a
  // one-shot pop-in on the incoming card and a pop-out on the outgoing one.
  const prevStepRef = useRef(0);
  useEffect(() => {
    const mapping = {
      1: slideRef.current,
      2: actRef.current,
      3: hwRef.current,
    };
    const incoming = mapping[step];
    const outgoing = mapping[prevStepRef.current];
    prevStepRef.current = step;

    const reset = (el, cls) => {
      if (!el) return;
      el.classList.remove(cls);
      // force reflow to retrigger the keyframe animation
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.classList.add(cls);
      const t = setTimeout(() => el.classList.remove(cls), 760);
      return () => clearTimeout(t);
    };
    const cleanups = [];
    if (outgoing && outgoing !== incoming) {
      const c = reset(outgoing, "is-popping-out");
      if (c) cleanups.push(c);
    }
    if (incoming && incoming !== outgoing) {
      const c = reset(incoming, "is-popping-in");
      if (c) cleanups.push(c);
    }
    return () => cleanups.forEach((fn) => fn());
  }, [step]);

  const safeStep = clampAiUiStep(step);
  const explainer = EXPLAINERS[safeStep];
  const words = transcriptWords[safeStep];

  return (
    <section ref={sectionRef} id="ai-tutor" className="sp-ai" data-sp-snap-section data-testid="ai-section">
      <div className="sp-ai-inner">
        <div className="sp-ai-bg" aria-hidden="true" />

        <div ref={introBlockRef} className="sp-ai-intro-stack">
          <div ref={titleRef} className="sp-ai-head" data-testid="ai-head">
          <h2 className="sp-ai-title">
            Your{" "}
            <span className="sp-grad-word">AI learning companion</span>{" "}
            after every live class.
          </h2>
          <p className="sp-ai-sub">
            Sparkl AI Tutor teaches concepts visually, makes students
            interact, checks handwritten work, and clears doubts when
            students need help.
          </p>
          </div>

          {/* Step tag above the workspace — step number + what's happening */}
          <div ref={explainerRef} className="sp-ai-steptag" data-testid="ai-steptag">
          <span className="sp-ai-steptag-num">
            STEP {String(safeStep + 1).padStart(2, "0")} / 05
          </span>
          <h3 className="sp-ai-steptag-title">{explainer.title}</h3>
          </div>

          <div ref={shellRef} className="sp-ai-shell" data-testid="ai-shell">
          {/* Main content video stack (slide / activity / handwritten) */}
          <div ref={mainWrapRef} className="sp-ai-layer sp-ai-main" data-testid="ai-main">
            <video ref={slideRef}
              src={ASSETS.slide}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
            <video ref={actRef}
              src={getAssetUrl(ASSETS.activityPath)}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
            <video ref={hwRef}
              src={ASSETS.handwritten}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
          </div>

          {/* AI Avatar stack (intro / teaching / doubt) */}
          <div ref={avatarWrapRef} className="sp-ai-layer sp-ai-avatar" data-testid="ai-avatar">
            <video ref={aiIntroRef}
              src={ASSETS.aiIntro}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
            <video ref={aiTeachRef}
              src={ASSETS.aiTeaching}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
            <video ref={aiDoubtRef}
              src={ASSETS.aiDoubt}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
            <div className="sp-ai-badge">AI Tutor</div>
          </div>

          {/* Student stack (teaching/activity/handwritten vs doubt) */}
          <div ref={studentWrapRef} className="sp-ai-layer sp-ai-student" data-testid="ai-student">
            <video ref={studentTeachRef}
              src={ASSETS.student}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded"
            />
            <video ref={studentDoubtRef}
              src={ASSETS.studentDoubt}
              muted loop playsInline autoPlay preload={isMobile ? "metadata" : "auto"}
              webkit-playsinline="true" x5-playsinline="true"
              className="sp-ai-video sp-ai-video-rounded sp-ai-video-doubt"
            />
          </div>

          {/* Hand raise icon — only visible during doubt step */}
          <div ref={handWrapRef} className="sp-ai-layer sp-ai-hand" data-testid="ai-hand">
            <img src={ASSETS.handIcon} alt="Hand raise" className="sp-ai-hand-img" />
          </div>
          </div>

          {/* Transcript panel — centered below the workspace, floating on the stage */}
          <div ref={transcriptRef} className="sp-ai-transcript" data-testid="ai-transcript">
          <span className="sp-ai-transcript-label">Live transcript</span>
          <p className="sp-ai-transcript-text">
              {words.slice(0, wordCount).map((w, i) => (
                <span
                  key={`${safeStep}-${i}`}
                  className={`sp-ai-word is-revealed ${
                    i === wordCount - 1 ? "is-latest" : ""
                  }`}
                >
                  {w}
                </span>
              ))}
            <span className="sp-ai-caret" aria-hidden="true" />
          </p>
          </div>
        </div>
      </div>
    </section>
  );
}
